import React, { PropsWithChildren, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@ui/components/Button'
import { FeatureIcon } from '@ui/components/FeatureIcon/FeatureIcon'
import { Modal } from '@ui/components/Modal'
import { AlertIcon } from '@ui/icons/Alert.icon'
import { ArrowCircleIcon } from '@ui/icons/ArrowCircle.icon'
import { QRCodeIcon } from '@ui/icons/QRCode.icon'

import { PaymentProviderQrDto } from '@model/MasterTransactionDto'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { Box } from '@shared/components/Box'

import { QrCodeDisplay } from '../../components/QrCodeDisplay/QrCodeDisplay'
import { getDepositCurrency } from '../../helpers'
import { DepositProceedValues } from '../../hooks/useDepositProceedRedirect'
import { useQrDepositStatusListener } from '../../hooks/useQrDepositStatusListener'
import { OrSeparator } from './OrSeparator'

interface ProceedModalContentProps extends PropsWithChildren {
  values: DepositProceedValues
  qr?: PaymentProviderQrDto | null
  transactionId?: string
  onProceed(): void
  close(): void
  onQrSuccess?(): void
  onQrDeclined?(): void
}

export const ProceedModalContent: React.FC<ProceedModalContentProps> = ({
  values,
  qr,
  transactionId,
  onProceed,
  close,
  onQrSuccess,
  onQrDeclined,
  children,
}) => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [showQr, setShowQr] = useState(false)
  const { formatMoney } = useFormatNumber()

  useQrDepositStatusListener({
    transactionId,
    enabled: showQr,
    onSuccess: () => {
      close()
      onQrSuccess?.()
    },
    onDeclined: () => {
      close()
      onQrDeclined?.()
    },
  })

  if (!values) return null

  const currency = getDepositCurrency(values)
  const hasQr = Boolean(qr?.prepared)

  const handleProceed = async () => {
    setIsLoading(true)
    try {
      await Promise.resolve(onProceed())
    } finally {
      close()
    }
  }

  return (
    <Modal mode={hasQr ? 'popup' : undefined}>
      <Modal.Body centered hideScrollShadows={showQr}>
        <FeatureIcon size='md' icon={AlertIcon} bg='surface-orange-high-em' />
        <Modal.Body.Title color='text-high-em'>
          {`${t('Deposit of')} ${formatMoney(values.amount, currency)}`}
        </Modal.Body.Title>
        {showQr && qr?.prepared ? (
          <Box flex justify='center' fullWidth>
            <QrCodeDisplay qr={qr} />
          </Box>
        ) : (
          children
        )}
      </Modal.Body>
      <Modal.Footer>
        {hasQr ? (
          <Box flex direction='column' gap={showQr ? 'x8' : 'x16'} fullWidth>
            {!showQr && (
              <Button.Primary
                size='lg'
                fullWidth
                iconRight={QRCodeIcon}
                onClick={() => setShowQr(true)}
              >
                {t('Scan QR Code')}
              </Button.Primary>
            )}
            <OrSeparator />
            <Button.Secondary
              size='lg'
              fullWidth
              iconRight={ArrowCircleIcon.UpRight}
              onClick={handleProceed}
              loading={isLoading}
              disabled={isLoading}
            >
              {t('Proceed')}
            </Button.Secondary>
          </Box>
        ) : (
          <Modal.Footer.Primary
            iconRight={ArrowCircleIcon.UpRight}
            onClick={handleProceed}
            loading={isLoading}
            disabled={isLoading}
          >
            {t('Proceed')}
          </Modal.Footer.Primary>
        )}
      </Modal.Footer>
    </Modal>
  )
}
